import * as React from 'react';
import flatten from 'lodash/flatten';
import { useParams } from 'react-router';
import { useLocalStorage } from 'usehooks-ts';
import { useFeed } from '@sites/contexts/feedContext';

type ContinueWatchingVideo = {
  currentTime: number;
  duration: number;
  lastUpdated: number;
  playlistId: string;
};

type ContinueWatching = Record<string, ContinueWatchingVideo>;

function notEmpty<TValue>(value: TValue | null | undefined): value is TValue {
  return value !== null && value !== undefined;
}

const defaultContinueWatching: ContinueWatching = {};

export function useContinueWatching() {
  const { customerSite } = useParams<'customerSite'>();
  const [continueWatching, setContinueWatching] = useLocalStorage(
    `resitv:${customerSite}:currentlyWatching`,
    defaultContinueWatching
  );
  const { videos } = useFeed();

  const getContinueWatchingVideos = React.useCallback(
    () =>
      Object.entries(continueWatching)
        .sort(([, videoA], [, videoB]) => videoB.lastUpdated - videoA.lastUpdated)
        .map(([videoId]) => flatten([...videos.values()]).find((video) => video.id === videoId))
        .filter(notEmpty),
    [continueWatching, videos]
  );

  const getContinueWatchingVideo = React.useCallback(
    (videoId: string | undefined): ContinueWatchingVideo | undefined =>
      videoId ? continueWatching[videoId] : undefined,
    [continueWatching]
  );

  const removeContinueWatchingVideo = React.useCallback(
    (videoId: string) => {
      setContinueWatching((current) => {
        const { [videoId]: deleted, ...rest } = current;
        return { ...rest };
      });
    },
    [setContinueWatching]
  );

  const setContinueWatchingVideo = React.useCallback(
    ({
      currentTime,
      duration,
      playlistId,
      videoId,
    }: {
      currentTime: number;
      duration: number;
      playlistId: string;
      videoId: string;
    }) => {
      setContinueWatching((current) => ({
        ...current,
        [videoId]: { lastUpdated: Date.now(), currentTime, duration, playlistId },
      }));
    },
    [setContinueWatching]
  );

  return {
    continueWatching,
    getContinueWatchingVideos,
    getContinueWatchingVideo,
    removeContinueWatchingVideo,
    setContinueWatchingVideo,
  };
}
